import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import "@/App.css";
import {
  createBrowserRouter, RouterProvider,
  Navigate, useLocation, useNavigate, Outlet,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import NewVersionBar from "@/components/NewVersionBar";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
import { GoogleOAuthProvider } from "@react-oauth/google";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import Expenses from "@/pages/Expenses";
import BookingCalendar from "@/pages/BookingCalendar";
import Inbox from "@/pages/Inbox";
import PropertyEvaluator from "@/pages/PropertyEvaluator";
import PropertyFinder from "@/pages/PropertyFinder";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import Tasks from "@/pages/Tasks";
import Integrations from "@/pages/Integrations";
import Pricing from "@/pages/Pricing";
import GSTCompliance from "@/pages/GSTCompliance";
import BookingPage from "@/pages/BookingPage";
import HotelBookingPage from "@/pages/HotelBookingPage";
import ExtendOffer from "@/pages/ExtendOffer";
import CaretakerVerify from "@/pages/CaretakerVerify";
import GuestGuidebook from "@/pages/GuestGuidebook";
import SetupAdvisor from "@/pages/SetupAdvisor";
import LegalHub, { LegalContent } from "@/pages/LegalHub";
import AdminPanel from "@/pages/AdminPanel";
import Landing from "@/pages/LandingV3";
import LandingV2 from "@/pages/LandingV2";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import FAQ from "@/pages/FAQ";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import PricingAlert from "@/pages/PricingAlert";
import GuestReview from "@/pages/GuestReview";
import { PropertyFilterProvider } from "@/context/PropertyFilter";
import GSTCalculator from "@/pages/GSTCalculator";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Layout from "@/components/Layout";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Single QueryClient instance — shared across the whole app
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:           2 * 60 * 1000,  // 2 min — data is "fresh", no background refetch
      gcTime:             10 * 60 * 1000,  // 10 min — keep in cache after last observer
      retry:               1,              // one retry on network failure
      refetchOnWindowFocus: false,         // don't refetch just because user switches tab
    },
  },
});

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/auth/me`, { credentials: "include", headers });
      if (res.ok) {
        setUser(await res.json());
      } else {
        localStorage.removeItem('session_token');
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const navigate = useNavigate();
  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    localStorage.removeItem('session_token');
    setUser(null);
    navigate("/");
  };

  const switchWorkspace = useCallback(async (staffId) => {
    const token = localStorage.getItem('session_token');
    const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const res = await fetch(`${API}/auth/switch-workspace`, {
      method: "POST", credentials: "include", headers,
      body: JSON.stringify({ staff_id: staffId || null }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.session_token) localStorage.setItem('session_token', data.session_token);
      await checkAuth();
    }
  }, [checkAuth]);

  // Returns true if current user can edit the given page key
  const canEdit = useCallback((page) => {
    if (!user) return false;
    if (!user.is_staff) return true;
    const perms = user.permissions || {};
    if (Array.isArray(perms)) return perms.includes(page);
    return (perms[page] || "none") === "edit";
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, switchWorkspace, canEdit }}>
      {children}
    </AuthContext.Provider>
  );
}

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="spinner" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen surface-base flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return <Outlet />;
}

function AdminRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.plan !== "admin") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function OwnerRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.is_staff) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function ToasterWithTheme() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors />;
}

// Root layout — wraps ALL routes with providers so every page can use useAuth / useTheme.
// createBrowserRouter requires providers to be inside the route tree (via Outlet).
function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <NewVersionBar />
          <Outlet />
          <ToasterWithTheme />
          <Analytics />
          <SpeedInsights />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/login",              element: <LoginPage /> },
      { path: "/auth/callback",      element: <AuthCallback /> },
      { path: "/reset-password",     element: <ResetPasswordPage /> },
      { path: "/verify-email",       element: <VerifyEmail /> },
      { path: "/privacy",            element: <PrivacyPolicy /> },
      { path: "/terms",              element: <TermsOfService /> },
      { path: "/faq",               element: <FAQ /> },
      { path: "/book/:propertyId",          element: <BookingPage /> },
      { path: "/book/hotel/:propertyId",    element: <HotelBookingPage /> },
      { path: "/book/extend/:token",        element: <ExtendOffer /> },
      { path: "/checkin/:bookingId",        element: <CaretakerVerify /> },
      { path: "/guide/:bookingId",   element: <GuestGuidebook /> },
      { path: "/gst-calculator",     element: <GSTCalculator /> },
      { path: "/legal",              element: <LegalHub /> },
      { path: "/blog",               element: <Blog /> },
      { path: "/blog/:slug",         element: <BlogPost /> },
      { path: "/accept-invite",      element: <AcceptInvite /> },
      { path: "/pricing-alert/:token", element: <PricingAlert /> },
      { path: "/review/:bookingId",  element: <GuestReview /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <Layout />,
            children: [
              { path: "/dashboard",     element: <Dashboard /> },
              { path: "/properties",    element: <Properties /> },
              { path: "/expenses",      element: <Expenses /> },
              { path: "/calendar",      element: <BookingCalendar /> },
              { path: "/inbox",         element: <Inbox /> },
              { path: "/evaluator",     element: <PropertyEvaluator /> },
              { path: "/finder",        element: <PropertyFinder /> },
              { path: "/tasks",         element: <Tasks /> },
              { path: "/integrations",  element: <Integrations /> },
              { path: "/pricing",       element: <Pricing /> },
              { path: "/gst",           element: <GSTCompliance /> },
              { path: "/setup-advisor", element: <SetupAdvisor /> },
              { path: "/legal-guide",   element: <LegalContent /> },
              {
                element: <OwnerRoute />,
                children: [
                  { path: "/settings", element: <Settings /> },
                  { path: "/billing",  element: <Billing /> },
                ],
              },
            ],
          },
          {
            element: <AdminRoute />,
            children: [
              {
                element: <Layout />,
                children: [
                  { path: "/admin", element: <AdminPanel /> },
                ],
              },
            ],
          },
        ],
      },
      { path: "/",       element: <LandingOrDashboard /> },
      { path: "/preview", element: <LandingV2 /> },
      { path: "*",       element: <Navigate to="/" replace /> },
    ],
  },
]);

function App() {
  return (
    <PropertyFilterProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <RouterProvider router={router} />
      </GoogleOAuthProvider>
    </PropertyFilterProvider>
  );
}

export default App;
